import React, { useEffect, useMemo, useState } from "react";
import "./OpportunityModal.css";
import { authFetch } from "../../utils/authFetch";
import CustomFieldsSection from "../custom-fields/CustomFieldsSection";

const PLAN_ID_COVERAGE_TYPES = ["MAPD", "PDP"];

const getPlanYearFromDate = (dateValue) => {
  if (!dateValue) return "";
  const year = new Date(`${dateValue}T00:00:00`).getFullYear();
  return Number.isNaN(year) ? "" : String(year);
};

const getCarrierId = (carrier) =>
  carrier?.id ?? carrier?.carrier_id ?? carrier?.carrierId ?? "";

const OpportunityModal = ({
  opportunity = {},
  onClose,
  onSave,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    id: null,
    status: "",
    carrierId: "",
    planName: "",
    planId: "",
    coverageTypeId: "",
    coverageTypePlanTypeId: "",
    applicationDate: "",
    effectiveDate: "",
    dueDate: "",
    electionPeriod: "",
    nextStep: "",
    policyNumber: "",
    enrollmentCode: "",
    premium: "",
    benefitOption: "",
    faceValue: "",
    benefitAmount: "",
    termLength: ""
  });

  const [coverageTypes, setCoverageTypes] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [opportunityStatuses, setOpportunityStatuses] = useState([]);
  const [coverageTypeLoading, setCoverageTypeLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showConversionConfirm, setShowConversionConfirm] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    setFormData({
      id: opportunity?.id || null,
      status: opportunity?.status || "",
      carrierId: opportunity?.carrier_id || opportunity?.carrierId || "",
      planName:
        opportunity?.plan_name ||
        opportunity?.planName ||
        opportunity?.product_name ||
        opportunity?.productName ||
        "",
      planId: opportunity?.plan_id || opportunity?.planId || "",
      coverageTypeId: opportunity?.coverage_type_id || opportunity?.coverageTypeId || "",
      coverageTypePlanTypeId:
        opportunity?.coverage_type_plan_type_id ||
        opportunity?.coverageTypePlanTypeId ||
        "",
      applicationDate: (opportunity?.application_date || opportunity?.applicationDate || "").split("T")[0],
      effectiveDate: (opportunity?.effective_date || opportunity?.effectiveDate || "").split("T")[0],
      dueDate: (opportunity?.due_date || opportunity?.dueDate || "").split("T")[0],
      electionPeriod: opportunity?.election_period || opportunity?.electionPeriod || "",
      nextStep: opportunity?.next_step || opportunity?.nextStep || "",
      policyNumber: opportunity?.policy_number || opportunity?.policyNumber || "",
      enrollmentCode: opportunity?.enrollment_code || opportunity?.enrollmentCode || "",
      premium: opportunity?.premium || "",
      benefitOption: opportunity?.benefit_option || opportunity?.benefitOption || "",
      faceValue: opportunity?.face_value || opportunity?.faceValue || "",
      benefitAmount: opportunity?.benefit_amount || opportunity?.benefitAmount || "",
      termLength: opportunity?.term_length || opportunity?.termLength || ""
    });
  }, [opportunity]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [coverageRes, statusesRes] = await Promise.all([
          authFetch("/api/coverage-types"),
          authFetch("/api/policy-statuses?type=opportunity")
        ]);

        const coverageData = await coverageRes.json().catch(() => []);
        const statusesData = await statusesRes.json().catch(() => []);

        setCoverageTypes(Array.isArray(coverageData) ? coverageData : []);
        setOpportunityStatuses(Array.isArray(statusesData) ? statusesData : []);
      } catch (err) {
        console.error("Load opportunity modal lookup data error:", err);
        setCoverageTypes([]);
        setOpportunityStatuses([]);
        setCarriers([]);
      }
    };

    loadInitialData();
  }, []);

  const selectedCoverageType = useMemo(
    () => coverageTypes.find((item) => String(item.id) === String(formData.coverageTypeId)),
    [coverageTypes, formData.coverageTypeId]
  );

  const selectedCoverageName = selectedCoverageType?.name || "";
  const shouldShowPlanId = PLAN_ID_COVERAGE_TYPES.includes(selectedCoverageName);
  const shouldShowPlanName = selectedCoverageName !== "Medicare Supplement";

  useEffect(() => {
    const loadCoverageTypeData = async () => {
      if (!formData.coverageTypeId) {
        setPlanTypes([]);
        setCarriers([]);
        return;
      }

      try {
        setCoverageTypeLoading(true);

        const [planTypesRes, carriersRes] = await Promise.all([
          authFetch(`/api/coverage-types/${formData.coverageTypeId}/plan-types`),
          authFetch(`/api/subscriber-carriers/coverage-type/${formData.coverageTypeId}`)
        ]);

        const planTypesData = await planTypesRes.json().catch(() => []);
        const carriersData = await carriersRes.json().catch(() => ({}));

        const nextPlanTypes = Array.isArray(planTypesData) ? planTypesData : [];

        const nextCarriers = Array.isArray(carriersData?.carriers)
          ? carriersData.carriers
          : Array.isArray(carriersData)
            ? carriersData
            : [];

        setPlanTypes(nextPlanTypes);
        setCarriers(nextCarriers);

        setFormData((prev) => {
          if (!prev.carrierId) return prev;

          const carrierStillAvailable = nextCarriers.some(
            (carrier) => String(getCarrierId(carrier)) === String(prev.carrierId)
          );

          if (carrierStillAvailable) return prev;

          return {
            ...prev,
            carrierId: "",
            benefitOption: "",
            benefitAmount: ""
          };
        });
      } catch (err) {
        console.error("Load opportunity coverage type data error:", err);
        setPlanTypes([]);
        setCarriers([]);
      } finally {
        setCoverageTypeLoading(false);
      }
    };

    loadCoverageTypeData();
  }, [formData.coverageTypeId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "coverageTypeId") {
        return {
          ...prev,
          coverageTypeId: value,
          coverageTypePlanTypeId: "",
          carrierId: "",
          planName: "",
          planId: "",
          benefitOption: "",
          benefitAmount: "",
          termLength: "",
          faceValue: ""
        };
      }

      if (name === "carrierId") {
        return {
          ...prev,
          carrierId: value,
          benefitOption: "",
          benefitAmount: ""
        };
      }

      if (name === "coverageTypePlanTypeId") {
        return {
          ...prev,
          coverageTypePlanTypeId: value,
          benefitAmount: "",
          termLength: "",
          faceValue: ""
        };
      }

      return {
        ...prev,
        [name]: value
      };
    });
  };

  const selectedPlanType = useMemo(
    () => planTypes.find((item) => String(item.id) === String(formData.coverageTypePlanTypeId)),
    [planTypes, formData.coverageTypePlanTypeId]
  );

  const selectedCarrier = useMemo(
    () => carriers.find((item) => String(getCarrierId(item)) === String(formData.carrierId)),
    [carriers, formData.carrierId]
  );

  const modalTitle = selectedCoverageType?.name
    ? `${selectedCoverageType.name} Opportunity`
    : "Edit Opportunity";

  const summaryItems = [
    { label: "Status", value: formData.status || "Not Set" },
    { label: "Next Step", value: formData.nextStep || "None" },
    { label: "Carrier", value: selectedCarrier?.name || selectedCarrier?.carrier_name || "Not Set" },
    {
      label: selectedCoverageType?.name === "Medicare Supplement" ? "Plan Type" : "Plan",
      value:
        selectedCoverageType?.name === "Medicare Supplement"
          ? selectedPlanType?.name || "Not Set"
          : formData.planName || "Not Set"
    }
  ];

  const renderCarrierSelect = () => (
    <div className="form-group">
      <label>Carrier</label>
      <select
        name="carrierId"
        value={formData.carrierId}
        onChange={handleChange}
        disabled={!formData.coverageTypeId || coverageTypeLoading || carriers.length === 0}
      >
        <option value="">
          {coverageTypeLoading
            ? "-- Loading carriers --"
            : carriers.length === 0
              ? "-- No carriers configured --"
              : "-- Select Carrier --"}
        </option>
        {carriers.map((c) => {
          const carrierId = getCarrierId(c);

          return (
            <option key={carrierId} value={carrierId}>
              {c.name || c.carrier_name}
            </option>
          );
        })}
      </select>

      {formData.coverageTypeId && !coverageTypeLoading && carriers.length === 0 && (
        <small>
          No carriers have been selected for this coverage type. Manage carriers in Settings.
        </small>
      )}
    </div>
  );

  const renderPlanNameInput = () => (
    <div className="form-group">
      <label>Plan Name</label>
      <input
        type="text"
        name="planName"
        value={formData.planName}
        onChange={handleChange}
        placeholder="Enter plan/product name"
      />
    </div>
  );

  const renderPlanIdInput = () =>
    shouldShowPlanId ? (
      <div className="form-group">
        <label>Plan ID</label>
        <input
          type="text"
          name="planId"
          value={formData.planId}
          onChange={handleChange}
          placeholder="Example: H1234-001"
        />
      </div>
    ) : null;

  const renderCoverageSpecificFields = () => {
    switch (selectedCoverageType?.name) {
      case "MAPD":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
            {renderPlanIdInput()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select name="electionPeriod" value={formData.electionPeriod} onChange={handleChange}>
                <option value="">-- Select Period --</option>
                <option value="IEP">IEP</option>
                <option value="ICEP">ICEP</option>
                <option value="OEP">OEP</option>
                <option value="AEP">AEP</option>
                <option value="SEP">SEP</option>
              </select>
            </div>
          </>
        );

      case "PDP":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
            {renderPlanIdInput()}
          </>
        );

      case "Dental":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            <div className="form-group">
              <label>Benefit Amount</label>
              <input
                type="text"
                name="benefitAmount"
                value={formData.benefitAmount}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </>
        );

      case "Medicare Supplement":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <select
                name="coverageTypePlanTypeId"
                value={formData.coverageTypePlanTypeId}
                onChange={handleChange}
              >
                <option value="">-- Select Plan Type --</option>
                {planTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {renderCarrierSelect()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select name="electionPeriod" value={formData.electionPeriod} onChange={handleChange}>
                <option value="">-- Select Period --</option>
                <option value="Open Enrollment">Open Enrollment</option>
                <option value="Underwritten">Underwritten</option>
                <option value="Guaranteed Issue">Guaranteed Issue</option>
              </select>
            </div>
          </>
        );

      case "Life":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <select
                name="coverageTypePlanTypeId"
                value={formData.coverageTypePlanTypeId}
                onChange={handleChange}
              >
                <option value="">-- Select Plan Type --</option>
                {planTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            <div className="form-group">
              <label>Face Value</label>
              <input
                type="number"
                name="faceValue"
                value={formData.faceValue}
                onChange={handleChange}
              />
            </div>
          </>
        );

      case "Individual Health":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <select
                name="coverageTypePlanTypeId"
                value={formData.coverageTypePlanTypeId}
                onChange={handleChange}
              >
                <option value="">-- Select Plan Type --</option>
                {planTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            {selectedPlanType?.name === "Short-Term Health" && (
              <>
                <div className="form-group">
                  <label>Benefit Amount</label>
                  <input
                    type="number"
                    name="benefitAmount"
                    value={formData.benefitAmount}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Term Length (months)</label>
                  <input
                    type="number"
                    name="termLength"
                    value={formData.termLength}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </>
        );

      default:
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
          </>
        );
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    const payload = {
      id: formData.id,
      status: formData.status || null,
      carrier_id: parseInt(formData.carrierId, 10) || null,
      product_id: null,
      plan_name: shouldShowPlanName ? formData.planName || null : null,
      plan_id: shouldShowPlanId ? formData.planId || null : null,
      coverage_type_id: parseInt(formData.coverageTypeId, 10) || null,
      coverage_type_plan_type_id: formData.coverageTypePlanTypeId
        ? parseInt(formData.coverageTypePlanTypeId, 10)
        : null,
      application_date: formData.applicationDate || null,
      effective_date: formData.effectiveDate || null,
      due_date: formData.dueDate || null,
      election_period: formData.electionPeriod || null,
      next_step: formData.nextStep || null,
      policy_number: formData.policyNumber || null,
      enrollment_code: formData.enrollmentCode || null,
      premium: formData.premium || null,
      benefit_option: formData.benefitOption || null,
      face_value: formData.faceValue || null,
      benefit_amount: formData.benefitAmount || null,
      term_length: formData.termLength || null,
      plan_type: selectedPlanType?.name || null,
      health_plan_type:
        selectedCoverageType?.name === "Individual Health" ? selectedPlanType?.name || null : null,
      plan_year: getPlanYearFromDate(formData.effectiveDate) || null
    };

    try {
      const savedOpportunity = await onSave(payload);

      setSaveMessage("Opportunity updated successfully.");

      const shouldPromptConversion =
        savedOpportunity?.shouldPromptConversion ||
        (
          formData.status === "Approved" &&
          !opportunity?.is_converted &&
          !opportunity?.converted_to_policy_id
        );

      if (shouldPromptConversion) {
        setShowConversionConfirm(true);
      }

      setTimeout(() => setSaveMessage(""), 2200);
    } catch (err) {
      console.error("Save opportunity error:", err);
      setSaveMessage(err.message || "Failed to save opportunity.");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToPolicy = async () => {
    if (!formData.id) return;

    try {
      setConverting(true);
      setConversionResult(null);

      const res = await authFetch(`/api/opportunities/${formData.id}/convert-to-policy`, {
        method: "POST"
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to convert opportunity.");
      }

      setConversionResult(data);
      setShowConversionConfirm(false);
      setSaveMessage(
        data.wasLeadConverted
          ? "Lead converted to client and approved policy created."
          : "Approved policy created successfully."
      );

      setTimeout(() => {
        setSaveMessage("");
        onClose();
      }, 1800);
    } catch (err) {
      console.error("Convert opportunity error:", err);
      setConversionResult({
        error: err.message || "Failed to convert opportunity."
      });
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(formData.id, formData.coverageTypeId);
    setDeleting(false);
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal modal-lg opportunity-modal redesigned-opportunity-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-shell">
            <div className="modal-header opportunity-modal-header">
              <div>
                <h3>{modalTitle}</h3>
                <p className="modal-subtitle">
                  Update pipeline status, quoted carrier, plan name, dates, and financial details.
                </p>
              </div>

              <button type="button" className="modal-close-btn" onClick={onClose}>
                ×
              </button>
            </div>

            <div className="modal-body opportunity-modal-body">
              <div className="opportunity-summary-row">
                {summaryItems.map((item) => (
                  <div key={item.label} className="opportunity-summary-card">
                    <div className="opportunity-summary-label">{item.label}</div>
                    <div className="opportunity-summary-value">{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="opportunity-edit-panel">
                <div className="opportunity-panel-head">
                  <h4>Edit Opportunity</h4>
                  <p>Everything important in one compact workspace.</p>
                </div>

                <div className="opportunity-form-grid">
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status} onChange={handleChange}>
                      <option value="">-- Select Status --</option>
                      {opportunityStatuses.map((status) => (
                        <option key={status.id} value={status.name}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Next Step</label>
                    <select name="nextStep" value={formData.nextStep} onChange={handleChange}>
                      <option value="">-- Select Step --</option>
                      <option value="Follow Up">Follow Up</option>
                      <option value="Quote">Quote</option>
                      <option value="Submit Application">Submit Application</option>
                      <option value="Waiting on Documents">Waiting on Documents</option>
                      <option value="Review">Review</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Coverage Type</label>
                    <select name="coverageTypeId" value={formData.coverageTypeId} onChange={handleChange}>
                      <option value="">-- Select Coverage Type --</option>
                      {coverageTypes.map((coverageType) => (
                        <option key={coverageType.id} value={coverageType.id}>
                          {coverageType.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {renderCoverageSpecificFields()}

                  <div className="form-group">
                    <label>Application Date</label>
                    <input
                      type="date"
                      name="applicationDate"
                      value={formData.applicationDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Effective Date</label>
                    <input
                      type="date"
                      name="effectiveDate"
                      value={formData.effectiveDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Policy Number</label>
                    <input
                      name="policyNumber"
                      value={formData.policyNumber}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Enrollment Code</label>
                    <input
                      name="enrollmentCode"
                      value={formData.enrollmentCode}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Premium</label>
                    <input
                      type="number"
                      name="premium"
                      value={formData.premium}
                      onChange={handleChange}
                    />
                  </div>

                  <CustomFieldsSection
                    recordType="opportunity"
                    recordId={opportunity.id}
                  />
                </div>
              </div>

              {saveMessage && (
                <div className="modal-status-message success">
                  {saveMessage}
                </div>
              )}

              {conversionResult?.error && (
                <div className="modal-status-message error">
                  {conversionResult.error}
                </div>
              )}
            </div>

            <div className="modal-footer opportunity-modal-footer">
              <div className="modal-footer-left">
                <button className="modal-danger-link" onClick={() => setShowDeleteConfirm(true)}>
                  Delete Opportunity
                </button>
              </div>

              <div className="modal-footer-right">
                <button className="modal-button" onClick={onClose}>
                  Close
                </button>
                <button className="modal-button save" onClick={handleSubmit} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConversionConfirm && (
        <div className="modal-overlay" onClick={() => setShowConversionConfirm(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-shell">
              <div className="modal-header">
                <div>
                  <h3>Convert Approved Opportunity?</h3>
                  <p className="modal-subtitle">
                    This opportunity has been saved as Approved.
                  </p>
                </div>

                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setShowConversionConfirm(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="modal-warning-card">
                  <strong>Would you like to convert this opportunity into a policy now?</strong>
                  <br />
                  <br />
                  If this record is still a lead, it will be converted to a client first. The
                  new policy will be created with the status <strong>Approved</strong>. You can
                  update it to Active later after confirming the policy goes in force.
                </div>
              </div>

              <div className="modal-footer">
                <div className="modal-footer-left" />
                <div className="modal-footer-right">
                  <button
                    className="modal-button"
                    onClick={() => setShowConversionConfirm(false)}
                    disabled={converting}
                  >
                    Save Approved Only
                  </button>

                  <button
                    className="modal-button save"
                    onClick={handleConvertToPolicy}
                    disabled={converting}
                  >
                    {converting ? "Converting..." : "Convert to Approved Policy"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-shell">
              <div className="modal-header">
                <div>
                  <h3>Delete Opportunity</h3>
                  <p className="modal-subtitle">
                    Remove this opportunity from the record permanently.
                  </p>
                </div>

                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="modal-warning-card">
                  <strong>Are you sure?</strong>
                  This action cannot be undone and will permanently remove the opportunity.
                </div>
              </div>

              <div className="modal-footer">
                <div className="modal-footer-left" />
                <div className="modal-footer-right">
                  <button className="modal-button" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </button>
                  <button className="modal-button danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? "Deleting..." : "Delete Opportunity"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OpportunityModal;